import React, { useRef, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Sphere, MeshDistortMaterial, Float, Icosahedron } from '@react-three/drei';
import { useTheme } from '../context/ThemeContext';

// Intersection Observer for fade-in animations
const useIntersectionObserver = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
      }
    );

    document.querySelectorAll('.fade-in').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
};

// Parallax effect on scroll
const useParallaxEffect = () => {
  useEffect(() => {
    const handleScroll = () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const scrolled = window.pageYOffset;
      document.querySelectorAll('.parallax').forEach((element) => {
        const speed = element.classList.contains('parallax-slow') ? 0.5 :
                     element.classList.contains('parallax-medium') ? 0.3 : 0.1;
        const yOffset = -(scrolled * speed);
        element.style.transform = `translate3d(0, ${yOffset}px, 0)`;
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
};

function AnimatedSphere() {
  const meshRef = useRef<THREE.Mesh>(null);
  const { theme } = useTheme();

  useFrame((state) => {
    if (meshRef.current && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      meshRef.current.rotation.x = state.clock.getElapsedTime() * 0.1;
      meshRef.current.rotation.y = state.clock.getElapsedTime() * 0.15;
    }
  });

  return (
    <Float
      speed={2}
      rotationIntensity={0.5}
      floatIntensity={1}
    >
      <Sphere ref={meshRef} args={[1.5, 64, 64]} scale={1.5}>
        <MeshDistortMaterial
          color={theme === 'dark' ? '#4D9FFF' : '#baddff'}
          attach="material"
          distort={0.3}
          speed={3}
          roughness={0.4}
          metalness={0.9}
          opacity={0.15}
          transparent
        />
      </Sphere>
    </Float>
  );
}

function FloatingIcosahedrons() {
  const { theme } = useTheme();

  return (
    <>
      {[...Array(5)].map((_, i) => (
        <Float
          key={i}
          speed={1.5}
          rotationIntensity={1}
          floatIntensity={2}
          position={[
            Math.sin(i * 2.4) * 3,
            Math.cos(i * 1.5) * 2,
            Math.sin(i * 3) * -2
          ]}
        >
          <Icosahedron args={[1, 0]} scale={0.4}>
            <meshPhongMaterial
              color={[
                '#4D9FFF',
                '#9333EA',
                '#EC4899',
                '#4D9FFF',
                '#9333EA'
              ][i]}
              opacity={0.15}
              transparent
              wireframe
            />
          </Icosahedron>
        </Float>
      ))}
    </>
  );
}

export function Background3D() {
  const { theme } = useTheme();
  useIntersectionObserver();
  useParallaxEffect();
  
  return (
    <div className={`fixed inset-0 -z-10 transition-opacity duration-1000 ${
      theme === 'dark' ? 'opacity-30' : 'opacity-20'
    }`}>
      <Canvas camera={{ position: [0, 0, 5] }}>
        <ambientLight intensity={0.5} />
        <directionalLight position={[10, 10, 5]} intensity={1} />
        <AnimatedSphere />
        <FloatingIcosahedrons />
      </Canvas>
      <div className={`absolute inset-0 ${
        theme === 'dark'
          ? 'bg-gradient-to-br from-dark-100/80 via-dark-200/80 to-dark-300/80'
          : 'bg-gradient-to-br from-gray-50/80 via-white/80 to-gray-100/80'
      }`} />

      {/* Geometric Shapes */}
      <div className="shape shape-rotate" style={{ top: '10%', left: '5%' }}>
        <div className="w-32 h-32 bg-primary-500/10 rounded-lg transform rotate-45" />
      </div>
      <div className="shape shape-float" style={{ top: '40%', right: '10%' }}>
        <div className="w-24 h-24 bg-accent-purple/10 rounded-full" />
      </div>
      <div className="shape shape-rotate" style={{ bottom: '15%', left: '15%' }}>
        <div className="w-40 h-40 bg-accent-pink/10 rounded-lg transform -rotate-12" />
      </div>
    </div>
  );
}